import { Tooltip } from "@material-ui/core";
import { createStyles, CSSProperties, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import deepmerge from "deepmerge";
import * as H from "history";
import * as React from "react";
import { Link as MuiLink } from "react-router-dom";

import { RelodeTheme } from "../";

const baseStyles = (theme: RelodeTheme) =>
    createStyles({
        root: {
            textDecoration: "none",
            display: "inline-block",
            cursor: "pointer",
        },
        content: {
            color: "unset",
        },
        internalHighlight: {
            textDecoration: "unset",
            transition: "box-shadow 400ms cubic-bezier(0.33, 0, 0, 1)",
            boxShadow: `0 2px 0px 0px ${theme.relode.colors.internalHighlight.main}, 0 0 0 0 ${theme.relode.colors.internalHighlight.main} inset`,
            "&:hover": {
                boxShadow: `0 2px 0px 0px ${theme.relode.colors.internalHighlight.main}, 0 -0.8rem 0 0 ${theme.relode.colors.internalHighlight.main} inset`,
            },
        },
        externalHighlight: {
            textDecoration: "unset",
            transition: "box-shadow 400ms cubic-bezier(0.33, 0, 0, 1)",
            boxShadow: `0 2px 0px 0px ${theme.relode.colors.externalHighlight.main}, 0 0 0 0 ${theme.relode.colors.externalHighlight.main} inset`,
            "&:hover": {
                boxShadow: `0 2px 0px 0px ${theme.relode.colors.externalHighlight.main}, 0 -0.8rem 0 0 ${theme.relode.colors.externalHighlight.main} inset`,
            },
        },
        gutterBottom: {
            marginBottom: 5,
        },
        linkText: {
            fontSize: "16px",
            color: theme.palette.primary.main,
        },
    });

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        deepmerge(baseStyles(theme), theme.relode.controls.link || {}),
    {
        name: "Link",
    }
);

type LinkCSSProperties =
    | "root"
    | "content"
    | "internalHighlight"
    | "externalHighlight"
    | "gutterBottom";

export type LinkStyles = Partial<
    Record<LinkCSSProperties, CSSProperties | ((props?: {}) => CSSProperties)>
>;

// original from admin
export interface LinkProps {
    id?: string;
    children?: React.ReactNode;
    className?: string;
    hideHighlight?: boolean;
    highlightType?: "internal" | "external";
    href?: string;
    target?: "_blank" | "_self" | "_parent" | "_top";
    title?: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    gutterBottom?: boolean;
    to?: H.LocationDescriptor;
    replace?: boolean;
    innerRef?: React.Ref<HTMLAnchorElement>;
    usePrimaryColorLink?: boolean;
    rel?: string;
}

export const Link = (props: LinkProps) => {
    const {
        children,
        className: classNameProp,
        gutterBottom = true,
        hideHighlight = false,
        highlightType = "internal",
        href,
        id,
        innerRef,
        onClick,
        replace,
        target,
        title = "",
        to,
        usePrimaryColorLink = false,
        ...others
    } = props;
    const classes = useStyles({});
    const className = classnames(
        classes.root,
        classes.content,
        {
            [classes.internalHighlight]:
                !hideHighlight && highlightType === "internal",
            [classes.externalHighlight]:
                !hideHighlight && highlightType === "external",
            [classes.gutterBottom]: gutterBottom,
            [classes.linkText]: usePrimaryColorLink,
        },
        classNameProp
    );

    const renderLink = () =>
        !!to ? (
            <MuiLink
                className={className}
                href={href}
                id={id}
                innerRef={innerRef}
                onClick={onClick}
                replace={replace}
                target={target}
                to={to}
                data-cy={`${id}-link`}
                {...others}
            >
                {children}
            </MuiLink>
        ) : (
            <a
                id={id}
                className={className}
                href={href}
                target={target}
                onClick={onClick}
                data-cy={`${id}-link`}
                {...others}
            >
                {children}
            </a>
        );

    return (
        <Tooltip placement="bottom-start" title={title}>
            {renderLink()}
        </Tooltip>
    );
};
